import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, of, switchMap, tap } from 'rxjs';
import { CachingApiService } from './cachingApiService';
import { BambooEmergencyContact, SeasonalStaffDetail, StaffContacts, UiSafeStaff, UiSafeStaffUpdate } from './commonInterfaces';
import { ConfigService } from '../config.service';
import { LocalStorageService } from '../storage/local-storage.service';
import { StaffRoles } from '../auth/tokens';
import { SetOptional } from 'type-fest';
import { IconDefinition } from '@fortawesome/pro-solid-svg-icons';
import { IconService } from '../ui/icon.service';
import { DataTransform } from './dataTransformFunctions';
import { PreferencesService } from '../preferences.service';
import { BroadcastService } from '../services/broadcast.service';

export type StaffListType = "all" | "active" | "inactive" | "active_ft" | "active_pt" | "staffing_tool_list"
export type StaffDisplayType = "firstAndLast" | "nicknameAndLast" | "firstAndLastWithNickname"
export type StaffIconMapFields = "roleft" | "rolept" | "driver" | "ml" | "active-status"
export type UpdateSeasonalStaffData = SetOptional<Omit<SeasonalStaffDetail, "id">, "location" | "notes" | "primaryOpsRegionId" | "flex">
export interface IconWithMetadata {
  icon: IconDefinition,
  tooltip: string
}
export interface SeasonalStaffDetailsUpdatedArgs {
  id: number,
  action: "updated" | "deleted"
  updatedDetail?: SeasonalStaffDetail
}

export interface StaffEmergencyContactsUpdatedArgs {
  staffId: number,
  contactId: number,
  action: "updated" | "added" | "deleted"
  updatedContact?: StaffContacts
}

export interface EmergencyContactsResponse {
  bambooContacts: BambooEmergencyContact[],
  contacts: StaffContacts[]
}


export interface StaffUpdatedArgs {
  staffId: number,
  action: "updated" | "added"
}
@Injectable({
  providedIn: 'root'
})
export class StaffService extends CachingApiService<UiSafeStaff[]> {
  protected broadcastKey: string = "staff"
  getApiUrl(): string {
    return this.config.dataApiUrl + "/staff"
  }

  constructor(
    http: HttpClient,
    config: ConfigService,
    storage: LocalStorageService,
    private preferences: PreferencesService,
    private icons: IconService,
     broadcastService: BroadcastService
  ) {
    super(http, config, storage, broadcastService)
  }

  private _seasonalStaffDetailsUpdatedSubject = new Subject<SeasonalStaffDetailsUpdatedArgs>()
  readonly seasonalStaffUpdated$ = this._seasonalStaffDetailsUpdatedSubject.asObservable()

  private staffUpdatedSubject = new Subject<StaffUpdatedArgs>()
  staffUpdated$ = this.staffUpdatedSubject.asObservable()

  private _staffEmergencyContactsUpdatedSubject = new Subject<StaffEmergencyContactsUpdatedArgs>()
  readonly staffEmergencyContactsUpdated$ = this._staffEmergencyContactsUpdatedSubject.asObservable()


  getEmptySeasonalStaffDetail(staffId = 0, year = new Date().getFullYear()): SeasonalStaffDetail {
    return {
      staffId: staffId,
      location: '',
      notes: '',
      id: 0,
      flex: 0
    }
  }

  getEmptyUiSafeStaff(): UiSafeStaff {
    return {
      active: 0,
      apptURL: "",
      city: "",
      driverNotes: "",
      email: "",
      first: "",
      id: -1,
      last: "",
      middle: "",
      nickname: "",
      phone1: "",
      roles: [],
      state: "",
      title: "",
      alwaysShowOnStaffing: 0,
      restricted: 0,
      supervisor: 0
    }
  }

  private getUsersSubject: BehaviorSubject<UiSafeStaff[]> = new BehaviorSubject<UiSafeStaff[]>([])

  getUsers(listType: StaffListType, roles: StaffRoles[] = ["everyone"]) {
    let returnObservable: Observable<UiSafeStaff[]>
    switch (listType) {
      case "all":
        returnObservable = this.getAllUsers();
        break;
      case 'active':
        returnObservable = this.getActiveUsers();
        break;
      case 'inactive':
        returnObservable = this.getInactiveUsers();
        break;
      case 'active_ft':
        returnObservable = this.getActiveFullTime();
        break;
      case 'active_pt':
        returnObservable = this.getActivePartTime();
        break;
      case 'staffing_tool_list':
        returnObservable = this.getStaffingToolList();
        break;
    }

    return returnObservable.pipe(
      switchMap(staff => {
        return of(staff.filter(s => s.roles.some(r => roles.includes(r))))
      })
    )

  }

  getTripLeadersByDateRange(fromDate: string, toDate: string) {
    const params = {
      fromDate,
      toDate
    }
    return this.http.get<UiSafeStaff[]>(`${this.config.dataApiUrl}/staff/trip-leaders`, { params })
  }

  getAllUsers() {
    return this.getCache().pipe(switchMap(cacheObj => of([...cacheObj.data])))
  }

  getActiveUsers() {
    return this.getCache().pipe(switchMap(cacheObj => of(cacheObj.data.filter(user => user.active === 1))))
  }

  getInactiveUsers() {
    return this.getCache().pipe(switchMap(cacheObj => of(cacheObj.data.filter(user => user.active === 0))))
  }

  getActiveFullTime() {
    return this.getCache().pipe(switchMap(cacheObj => of(cacheObj.data.filter(user => user.active === 1 && user.roles.includes("ft")))))
  }

  getActivePartTime() {
    return this.getCache().pipe(switchMap(cacheObj => of(cacheObj.data.filter(user => user.active === 1 && user.roles.includes("pt")))))
  }

  getConsultantList() {
    return this.getCache().pipe(switchMap(cacheObj => of(cacheObj.data.filter(user => user.active === 1 && user.roles.includes("consultant")))))
  }

  getRecruiterList() {
    return this.getCache().pipe(switchMap(cacheObj => of(cacheObj.data.filter(user => user.active === 1 && user.roles.includes("sales")))))
  }
  getTripLeaderList() {
    return this.getCache().pipe(switchMap(cacheObj => of(cacheObj.data.filter(user => user.active === 1 && user.roles.includes("tl")))))
  }
  getStaffingToolList() {
    return this.getCache().pipe(switchMap(cacheObj => of(cacheObj.data.filter(user => (user.active === 1 || user.alwaysShowOnStaffing === 1)))))
  }

  /**
   * Returns a list of users that meet the specified criteria.  Useful for populating user dropdowns based on context.
   * @param userIds User Id array to include always even if they are inactive or don't match any roles.  
   * Use this to include inactive users that may still be assigned to various values.
   * @param roles Array of roles the selected users must have to be included in the results. Everyone by default.
   * @returns 
   */
  getActiveUsersWithInactiveList(userIds: number[], roles: StaffRoles[] = ["everyone"]) {
    return this.getCache().pipe(
      switchMap(cacheObj =>
        of(
          cacheObj.data.filter(user => {
            return userIds.includes(user.id) ||
              (
                user.active === 1 && user.roles.some(role => roles.includes(role))
              )
          })
        )
      )
    )

  }

  getUserById(id: number) {
    return this.getCache().pipe(switchMap(users => {
      const user = users.data.find(u => u.id === id)
      return of(user)
    }

    ))
  }

  getUsersByIdList(ids: number[]) {
    return this.getCache().pipe(switchMap(users => {
      return of(users.data.filter(u => ids.includes(u.id)))
    }))
  }

  sortStaff(staff: UiSafeStaff[], displayType: StaffDisplayType = 'firstAndLast') {

    switch (displayType) {
      case "firstAndLast":
        return [...staff].sort((a, b) => (a.first + a.last).localeCompare(b.first + b.last))
      case "firstAndLastWithNickname":
        return [...staff].sort((a, b) => (a.first + a.nickname + a.last).localeCompare(b.first + b.nickname + b.last))
      case "nicknameAndLast":
        return [...staff].sort((a, b) => ((a.nickname ? a.nickname : a.first) + a.last).localeCompare((b.nickname ? b.nickname : b.first) + b.last))
    }

  }

  getUnassignedUser(): UiSafeStaff {
    return {
      active: 1,
      email: "",
      phone1: "",
      city: "",
      state: "",
      apptURL: "",
      first: "Unassigned",
      middle: "",
      last: "",
      id: 0,
      nickname: "Unassigned",
      roles: ['admin', 'atl', 'blog', 'conf', 'consultant', 'crisis', 'everyone', 'finance', 'ft', 'ftm', 'fund', 'head', 'hr', 'intern', 'it', 'ma', 'marketing', 'mc', 'ops', 'pt', 'sales', 'salesAdmin', 'super', 'tl', 'travel', 'intl', 'riskyTeamManage', 'walletAdmin', "driver"],
      title: "Unassigned",
      driverNotes: "",
      reviewSupervisorId: 0,
      alwaysShowOnStaffing: 0,
      restricted: 0,
    }
  }


  getSeasonalStaffDetails() {
    return this.http.get<SeasonalStaffDetail[]>(`${this.config.dataApiUrl}/staff/seasonal-details`)
  }

  updateSeasonalStaffDetails(data: UpdateSeasonalStaffData) {

    return this.http.put<SeasonalStaffDetail>(`${this.config.dataApiUrl}/staff/seasonal-details`, { ...data }).pipe(
      tap(detail => {
        this._seasonalStaffDetailsUpdatedSubject.next({ action: "updated", id: detail.id, updatedDetail: detail })
      })
    )

  }

  updateStaff(staffId: number, data: UiSafeStaffUpdate) {
    return this.http.put<UiSafeStaff>(`${this.config.dataApiUrl}/staff/${staffId.toString()}`, { ...data }).pipe(
      tap(staff => {
        this.getCache().subscribe((cache) => {
          const index = cache.data.findIndex(s => s.id === staff.id)
          this.setCacheData([...cache.data.slice(0, index), { ...staff }, ...cache.data.slice(index + 1)])
          this.staffUpdatedSubject.next({ staffId: staffId, action: "updated" })
        })

      })
    )
  }

  getEmergencyContacts(staffId: number) {
    return this.http.get<EmergencyContactsResponse>(`${this.config.dataApiUrl}/staff/${staffId}/emergency-contacts`).pipe(
      switchMap(response => {
        return of({
          bambooContacts: response.bambooContacts,
          contacts: DataTransform.replaceDatesArrayDirect(response.contacts, [{ dateType: "utc", fieldName: "timestamp" }])
        })
      })
    )
  }

  addEmergencyContact(staffId: number, contact: Pick<StaffContacts, "firstName" | "lastName" | "phone1" | "relation">) {
    return this.http.post<StaffContacts>(`${this.config.dataApiUrl}/staff/${staffId}/emergency-contacts`, { ...contact }).pipe(
      tap(returnContact => {
        this._staffEmergencyContactsUpdatedSubject.next({ staffId: staffId, contactId: returnContact.id, action: "added", updatedContact: returnContact })
      })
    )
  }

  updateEmergencyContact(staffId: number, contactId: number, contact: Pick<StaffContacts, "firstName" | "lastName" | "phone1" | "relation">) {
    return this.http.put<StaffContacts>(`${this.config.dataApiUrl}/staff/${staffId}/emergency-contacts/${contactId}`, { ...contact }).pipe(
      tap(returnContact => {
        this._staffEmergencyContactsUpdatedSubject.next({ staffId: staffId, contactId: contactId, action: "updated", updatedContact: returnContact })
      })
    )
  }

  deleteEmergencyContact(staffId: number, contactId: number) {
    return this.http.delete<void>(`${this.config.dataApiUrl}/staff/${staffId}/emergency-contacts/${contactId}`).pipe(
      tap(() => {
        this._staffEmergencyContactsUpdatedSubject.next({ staffId: staffId, contactId: contactId, action: "deleted" })
      })
    )
  }

  uploadProfilePhoto(staffId: number, photo: Blob) {


    const formData = new FormData()

    formData.append("files", photo)

    return this.http.post<void>(`${this.config.dataApiUrl}/staff/${staffId}/profile-photo`, formData)
  }

  hasProfilePhoto(staffId: number) {
    return this.http.get<boolean>(`${this.config.dataApiUrl}/staff/${staffId}/has-profile-photo`)
  }

  removeProfilePhoto(staffId: number) {
    return this.http.delete<void>(`${this.config.dataApiUrl}/staff/${staffId}/profile-photo`)
  }

  getStaffBypassCache(staffId: number) {
    return this.http.get<UiSafeStaff>(`${this.config.dataApiUrl}/staff/${staffId}`)
  }

  getSignatureHtml(staffId: number) {
    return this.http.get(`${this.config.dataApiUrl}/staff/${staffId}/signature-html`, {responseType: 'text'})
  }

  buildIconMap(staffList: UiSafeStaff[], fields: StaffIconMapFields[]) {
    const map = new Map<number, IconWithMetadata[]>()
    for (const field of fields) {
      for (const staff of staffList) {
        if (staff.id !== 0) {
          switch (field) {
            case "roleft":
              if (staff.roles.includes("ft") && !staff.roles.includes('ml')) map.set(staff.id, [...(map.get(staff.id) ?? []), { icon: this.icons.ppmSolid.fullTime, tooltip: "Year Round" }])
              break
            case "rolept":
              if (staff.roles.includes("pt")) map.set(staff.id, [...(map.get(staff.id) ?? []), { icon: this.icons.ppmSolid.partTime, tooltip: "Seasonal" }])
              break
            case "driver":
              if (staff.roles.includes("driver"))
                if (staff.roles.includes("driverVan")) map.set(staff.id, [...(map.get(staff.id) ?? []), { icon: this.icons.ppmSolid.driverVan, tooltip: "Van Driver" + (staff.driverNotes ? ": " + staff.driverNotes : "") }])
                else map.set(staff.id, [...(map.get(staff.id) ?? []), { icon: this.icons.ppmSolid.driver, tooltip: "Driver" + (staff.driverNotes ? ": " + staff.driverNotes : "") }])
              break
            case "ml":
              if (staff.roles.includes("ml")) map.set(staff.id, [...(map.get(staff.id) ?? []), { icon: this.icons.ppmSolid.ministryLeader, tooltip: "Ministry Leader" }])
              break
            case "active-status":
              if (staff.active === 0) map.set(staff.id, [...(map.get(staff.id) ?? []), { icon: this.icons.ppmSolid.inactiveUser, tooltip: "Inactive" }])
          }
        }
      }
    }

    return map
  }

  displayName(staff: Pick<UiSafeStaff, "first" | "last" | "nickname"> | undefined, options?: {unknownValue?: string}) {
    
    const unknownValue = options?.unknownValue ?? ""

     switch (this.preferences.staffDisplay) {
      case "firstAndLast":
        return staff ? staff.first + " " + staff.last : unknownValue
      case "nicknameAndLast":
        return staff ? (staff.nickname ? staff.nickname : staff.first) + " " + staff.last : unknownValue
      case "firstAndLastWithNickname":
        return staff ? staff.first + " " + (staff.nickname ? `(${staff.nickname}) ` : "") + staff.last : unknownValue
    }
  }

}
